import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { api, formatApiError } from "../lib/api";
import { Mail, X } from "lucide-react";

/**
 * Persistent banner shown to email-auth users whose email is not yet
 * verified. Offers a "Resend link" action, respects the 5-min server-side
 * cooldown, and can be temporarily dismissed for the current session.
 */
export default function VerifyEmailBanner() {
    const { user } = useAuth();
    const [dismissed, setDismissed] = useState(false);
    const [busy, setBusy] = useState(false);
    const [msg, setMsg] = useState("");
    const [tone, setTone] = useState("info"); // info | success | warn

    if (!user) return null;
    if (user.auth_method === "google") return null;
    if (user.is_verified) return null;
    if (dismissed) return null;

    const resend = async () => {
        setBusy(true); setMsg("");
        try {
            await api.post("/auth/verify-email/resend", { email: user.email });
            setTone("success");
            setMsg("New verification link sent. Check your inbox.");
        } catch (err) {
            const detail = err?.response?.data?.detail;
            setTone("warn");
            setMsg(formatApiError(detail));
        } finally {
            setBusy(false);
        }
    };

    const toneClasses = {
        info: "bg-amber-50 border-amber-200 text-amber-900",
        success: "bg-emerald-50 border-emerald-200 text-emerald-900",
        warn: "bg-amber-50 border-amber-300 text-amber-900",
    }[tone];

    return (
        <div
            className={`border-b ${toneClasses}`}
            data-testid="verify-email-banner"
            role="status"
        >
            <div className="sp-container flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6 py-3">
                <div className="flex items-start gap-3 flex-1">
                    <Mail strokeWidth={1.5} className="w-4 h-4 mt-0.5 shrink-0" />
                    <div className="text-sm leading-snug">
                        <span className="font-medium">Verify your email</span> to unlock trip alerts and secure your account.
                        {msg && (
                            <div className="mt-1 text-xs opacity-90" data-testid="verify-banner-msg">{msg}</div>
                        )}
                    </div>
                </div>
                <div className="flex items-center gap-3 pl-7 sm:pl-0">
                    <button
                        onClick={resend}
                        disabled={busy}
                        className="text-sm font-medium underline underline-offset-4 hover:no-underline disabled:opacity-60"
                        data-testid="verify-banner-resend"
                    >
                        {busy ? "Sending…" : "Resend link"}
                    </button>
                    <button
                        onClick={() => setDismissed(true)}
                        className="p-1 -m-1 opacity-70 hover:opacity-100"
                        aria-label="Dismiss"
                        data-testid="verify-banner-dismiss"
                    >
                        <X strokeWidth={1.5} className="w-4 h-4" />
                    </button>
                </div>
            </div>
        </div>
    );
}
